if (!customElements.get('localization-form')) {
  let localizationFormUid = 0;

  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();

        // Bind once (so we can also remove listeners cleanly)
        this._onContainerKeyUp = this.onContainerKeyUp.bind(this);
        this._onContainerKeyDown = this.onContainerKeyDown.bind(this);
        this._onFocusOut = this.closeSelector.bind(this);
        this._onButtonClick = this.onButtonClick.bind(this);

        this._onSearchInput = this.filterCountries.bind(this);
        this._onSearchFocus = this.onSearchFocus.bind(this);
        this._onSearchBlur = this.onSearchBlur.bind(this);
        this._onSearchKeyDown = this.onSearchKeyDown.bind(this);

        this._onCloseClick = this.hidePanel.bind(this);
        this._onResetClick = this.resetFilter.bind(this);

        // IMPORTANT: one stable handler for option links (prevents duplicate handlers on re-init)
        this._onOptionClick = this.onItemClick.bind(this);

        this.mql = window.matchMedia('(min-width: 750px)');
      }

      connectedCallback() {
        // Guard: prevent double init if the element is re-connected (theme editor, section reloads, etc.)
        if (this.dataset.localizationInit === 'true') return;
        this.dataset.localizationInit = 'true';

        this.header = document.querySelector('.header-wrapper');
        this.menuDrawer = document.querySelector('#menu-drawer') || document.querySelector('.menu-drawer');

        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('#sr-country-search-results'),
          countrySelector: this.querySelector('.country-selector'),
          countryList: this.querySelector('.country-selector__list'),
        };

        // If key elements are missing, fail silently (prevents console errors on pages without localisation UI)
        if (!this.elements.button || !this.elements.panel) return;

        // Ensure ARIA wiring is robust
        if (!this.elements.panel.id) {
          localizationFormUid += 1;
          this.elements.panel.id = `LocalizationPanel-${localizationFormUid}`;
        }
        this.elements.button.setAttribute('aria-controls', this.elements.panel.id);

        if (!this.elements.button.hasAttribute('aria-expanded')) {
          this.elements.button.setAttribute('aria-expanded', 'false');
        }

        // Keep panel state consistent with aria-expanded
        const expanded = this.elements.button.getAttribute('aria-expanded') === 'true';
        if (expanded) {
          this.elements.panel.removeAttribute('hidden');
        } else {
          this.elements.panel.setAttribute('hidden', true);
        }

        // Core listeners
        this.addEventListener('keyup', this._onContainerKeyUp);
        this.addEventListener('keydown', this._onContainerKeyDown);
        this.addEventListener('focusout', this._onFocusOut);

        // Toggle on click
        this.elements.button.addEventListener('click', this._onButtonClick);

        // Search interactions (if present)
        if (this.elements.search) {
          this.elements.search.addEventListener('input', this._onSearchInput);
          this.elements.search.addEventListener('focus', this._onSearchFocus);
          this.elements.search.addEventListener('blur', this._onSearchBlur);
          this.elements.search.addEventListener('keydown', this._onSearchKeyDown);

          // Ensure aria-activedescendant exists but starts empty
          this.elements.search.setAttribute('aria-activedescendant', '');
        }

        // Close/reset buttons (if present)
        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', this._onCloseClick);
        }
        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this._onResetClick);
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }

        // Bind option links only (stable handler)
        this.getOptionLinks().forEach((item) => item.addEventListener('click', this._onOptionClick));
      }

      disconnectedCallback() {
        // Best-effort cleanup (theme editor reloads)
        if (!this.elements) return;

        this.removeEventListener('keyup', this._onContainerKeyUp);
        this.removeEventListener('keydown', this._onContainerKeyDown);
        this.removeEventListener('focusout', this._onFocusOut);

        if (this.elements.button) this.elements.button.removeEventListener('click', this._onButtonClick);

        if (this.elements.search) {
          this.elements.search.removeEventListener('input', this._onSearchInput);
          this.elements.search.removeEventListener('focus', this._onSearchFocus);
          this.elements.search.removeEventListener('blur', this._onSearchBlur);
          this.elements.search.removeEventListener('keydown', this._onSearchKeyDown);
        }

        if (this.elements.closeButton) this.elements.closeButton.removeEventListener('click', this._onCloseClick);

        if (this.elements.resetButton) {
          this.elements.resetButton.removeEventListener('click', this._onResetClick);
        }

        // Remove option link handlers (now possible because handler is stable)
        this.getOptionLinks().forEach((item) => item.removeEventListener('click', this._onOptionClick));
      }

      // --- Helpers
      isExpanded() {
        return this.elements?.button?.getAttribute('aria-expanded') === 'true';
      }

      getOptionLinks() {
        return Array.from(this.querySelectorAll('button[data-value], a[data-value]'));
      }

      getFocusableOptionLinks() {
        return this.getOptionLinks().filter((item) => {
          const li = item.parentElement;
          return li ? !li.classList.contains('hidden') : true;
        });
      }

      setExpanded(expanded) {
        if (!this.elements?.button || !this.elements?.panel) return;

        this.elements.button.setAttribute('aria-expanded', expanded ? 'true' : 'false');

        if (expanded) {
          this.elements.panel.removeAttribute('hidden');
        } else {
          this.elements.panel.setAttribute('hidden', true);
        }
      }

showPanel() {
  if (!this.elements?.button || !this.elements?.panel) return;

  this.setExpanded(true);

  // Only apply this lock if the drawer isn't already open/locking scroll
  if (!document.documentElement.classList.contains('cbh-drawer-open')) {
    if (!document.body.classList.contains('overflow-hidden-tablet')) {
      document.body.classList.add('overflow-hidden-mobile');
    }
  }

  if (this.menuDrawer) this.menuDrawer.classList.add('country-selector-open');
  if (this.hasAttribute('data-prevent-hide') && this.header) this.header.preventHide = true;

  if (this.elements.search && this.mql.matches) {
    window.setTimeout(() => this.elements.search && this.elements.search.focus(), 0);
  }
}

hidePanel() {
  if (!this.elements?.button || !this.elements?.panel) return;

  this.setExpanded(false);

  if (this.elements.search) {
    this.elements.search.value = '';
    this.filterCountries();
    this.elements.search.setAttribute('aria-activedescendant', '');
  }

  // Only remove if we were responsible for it (drawer handles its own lock)
  if (!document.documentElement.classList.contains('cbh-drawer-open')) {
    document.body.classList.remove('overflow-hidden-mobile');
  }

  if (this.menuDrawer) this.menuDrawer.classList.remove('country-selector-open');
  if (this.header) this.header.preventHide = false;
}

      togglePanel() {
        if (!this.elements?.button || !this.elements?.panel) return;
        this.isExpanded() ? this.hidePanel() : this.showPanel();
      }

      onButtonClick(event) {
        event.preventDefault();
        this.togglePanel();
      }

      // --- Keyboard interactions
      onContainerKeyDown(event) {
        if (!this.isExpanded()) return;

        const focusableItems = this.getFocusableOptionLinks();
        if (!focusableItems.length) return;

        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus.focus();
            break;

          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus.focus();
            break;

          default:
            return;
        }

        if (!this.elements.search) return;

        window.setTimeout(() => {
          focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
          if (focusedItemIndex > -1 && focusableItems[focusedItemIndex].id) {
            this.elements.search.setAttribute('aria-activedescendant', focusableItems[focusedItemIndex].id);
          } else {
            this.elements.search.setAttribute('aria-activedescendant', '');
          }
        }, 0);
      }

      onContainerKeyUp(event) {
        const code = event.code.toUpperCase();

        switch (code) {
          case 'ESCAPE':
            if (!this.isExpanded()) return;
            event.preventDefault();
            event.stopPropagation();
            this.hidePanel();
            this.elements?.button?.focus();
            break;

          case 'SPACE':
            if (document.activeElement !== this.elements?.button) return;
            if (this.isExpanded()) return;
            event.preventDefault();
            this.showPanel();
            break;

          default:
            break;
        }
      }

      // --- Selection
      onItemClick(event) {
        event.preventDefault();
        const form = this.querySelector('form');

        if (!this.elements?.input) return;
        const value = event.currentTarget?.dataset?.value;
        if (!value) return;

        this.elements.input.value = value;

        if (form) form.submit();
      }

      closeSelector(event) {
  if (!this.isExpanded()) return;

  const toEl = event.relatedTarget;

  // If focus is moving to another element inside this component, do nothing
  if (toEl && this.contains(toEl)) return;

  // Defer and check the real activeElement (covers relatedTarget === null cases)
  setTimeout(() => {
    if (!this.isExpanded()) return;

    const ae = document.activeElement;
    if (ae && this.contains(ae)) return;

    this.hidePanel();
  }, 0);
}

      // --- Search/filter
      normalizeString(str) {
        try {
          return (str || '')
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '')
            .toLowerCase();
        } catch (e) {
          return (str || '').toLowerCase();
        }
      }

      filterCountries() {
        if (!this.elements?.search) return;

        const searchValue = this.normalizeString(this.elements.search.value);
        const popularCountries = this.querySelector('.popular-countries');
        const allCountries = this.getOptionLinks();

        let visibleCountries = 0;

        if (this.elements.resetButton) {
          this.elements.resetButton.classList.toggle('hidden', !searchValue);
        }

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', !!searchValue);
        }

        allCountries.forEach((item) => {
          const labelEl = item.querySelector('.country') || item;
          const countryName = this.normalizeString(labelEl?.textContent || '');
          const isMatch = !searchValue || countryName.indexOf(searchValue) > -1;

          if (item.parentElement) item.parentElement.classList.toggle('hidden', !isMatch);
          if (isMatch) visibleCountries += 1;
        });

        if (this.elements.liveRegion && window.accessibilityStrings?.countrySelectorSearchCount) {
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.countrySelectorSearchCount.replace(
            '[count]',
            visibleCountries
          );
        }

        if (this.elements.countrySelector) this.elements.countrySelector.scrollTop = 0;
        if (this.elements.countryList) this.elements.countryList.scrollTop = 0;
      }

      resetFilter(event) {
        event.preventDefault();
        event.stopPropagation();

        if (!this.elements?.search) return;

        this.elements.search.value = '';
        this.filterCountries();
        this.elements.search.focus();
      }

      onSearchFocus() {
        if (this.elements?.searchIcon) {
          this.elements.searchIcon.classList.add('country-filter__search-icon--hidden');
        }
      }

      onSearchBlur() {
        if (!this.elements?.search || !this.elements?.searchIcon) return;
        if (!this.elements.search.value) {
          this.elements.searchIcon.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if (event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
        }
      }
    }
  );
}
